import { useState } from 'react';
import { supabase } from './supabaseClient';
import BulkImportModal from './BulkImport';
import { CHROME_STORE_URL } from './links';
import { tzOptions, currencyOptions, detectTimezone, detectCurrency, tzLabel } from './regional';

// The wizard asks only what the app can't start without: who you are, and (optionally)
// your first client. Rates, billing rules, logo, address, team and the rest are the
// "Getting started" checklist inside the app (src/GettingStarted.js), where each item
// takes you to the right screen and can be done in any order, or later.

const C = {
  bg: '#0A1526', surface: '#16233F', border: '#28395C', muted: '#64748b',
  text: '#e2e8f0', textBright: '#f1f5f9', accent: '#00A0C8', danger: '#e53e3e', success: '#68d391',
};

const css = {
  page:  { minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', color:C.text, padding:'20px', boxSizing:'border-box' },
  wrap:  { width:'100%', maxWidth:'560px' },
  card:  { background:C.surface, border:`1px solid ${C.border}`, borderRadius:'14px', padding:'36px' },
  h1:    { fontSize:'22px', fontWeight:'800', color:C.textBright, marginBottom:'4px', margin:0 },
  sub:   { fontSize:'14px', color:C.muted, margin:'4px 0 24px', lineHeight:'1.5' },
  lbl:   { display:'block', fontSize:'11px', color:C.muted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'5px' },
  help:  { fontSize:'12px', color:C.muted, marginTop:'5px', lineHeight:'1.5' },
  inp:   { background:C.bg, border:`1px solid ${C.border}`, borderRadius:'7px', color:C.text, padding:'9px 12px', fontSize:'13px', width:'100%', outline:'none', boxSizing:'border-box' },
  sel:   { background:C.bg, border:`1px solid ${C.border}`, borderRadius:'7px', color:C.text, padding:'9px 12px', fontSize:'13px', width:'100%', outline:'none', boxSizing:'border-box', cursor:'pointer' },
  field: { marginBottom:'16px' },
  g2:    { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'12px', marginBottom:'16px' },
  row:   { display:'flex', gap:'10px', marginTop:'24px', justifyContent:'space-between', alignItems:'center' },
  btn:   (v='primary') => ({ padding:'10px 22px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor:'pointer', border:'none',
           background: v==='primary'?C.accent: v==='ghost'?'transparent':C.border,
           color: v==='ghost'?C.muted:'#fff' }),
  error: { color:C.danger, fontSize:'12px', marginBottom:'14px', padding:'8px 10px', background:'rgba(229,62,62,0.1)', borderRadius:'6px' },
  skip:  { fontSize:'12px', color:C.muted, cursor:'pointer', textDecoration:'underline', marginTop:'10px', display:'inline-block', background:'none', border:'none', padding:0, fontFamily:'inherit' },
  link:  { background:'none', border:'none', padding:0, color:C.accent, cursor:'pointer', fontSize:'inherit', fontFamily:'inherit' },
};

const MODEL_CARDS = [
  { id:'hourly',   label:'Hourly',          icon:'⏱', desc:'Bill by the hour. You log time and multiply by your rate. Good for variable or open-ended work.' },
  { id:'retainer', label:'Retainer',        icon:'📅', desc:'A flat monthly fee regardless of hours worked. Good for ongoing, predictable engagements.' },
  { id:'hybrid',   label:'Retainer + overage', icon:'⚖️', desc:'A flat monthly base that covers a set number of hours. Additional hours bill at an overage rate.' },
];

const TOTAL_STEPS = 4; // type, basics, first client, done

function ProgressBar({ step }) {
  const pct = Math.round(((step + 1) / TOTAL_STEPS) * 100);
  return (
    <div style={{ marginBottom:'28px' }} role="progressbar" aria-valuemin={1} aria-valuemax={TOTAL_STEPS} aria-valuenow={step + 1} aria-label="Setup progress">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
        <div style={{ fontSize:'11px', color:C.muted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.5px' }}>
          Step {step + 1} of {TOTAL_STEPS}
        </div>
        <div style={{ fontSize:'11px', color:C.muted }}>{pct}% complete</div>
      </div>
      <div style={{ height:'4px', borderRadius:'2px', background:C.border }}>
        <div style={{ height:'100%', borderRadius:'2px', background:C.accent, width:`${pct}%`, transition:'width 0.3s' }} />
      </div>
    </div>
  );
}

export default function Setup({ session, profile, org, onComplete, onSignOut = null, onBack = null, backLabel = '' }) {
  const [step, setStep]   = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');
  const [orgId, setOrgId] = useState(org?.id ?? null);

  // Step 0: account type
  const [accountType, setAccountType] = useState(org?.account_type === 'individual' ? 'individual' : '');

  // Step 1: basics. Timezone and currency start from the browser, not a fixed US default.
  const [firmName, setFirmName] = useState(org?.name || '');
  const [timezone, setTimezone] = useState(org?.timezone && org.timezone !== 'America/New_York' ? org.timezone : detectTimezone());
  const [currency, setCurrency] = useState(org?.currency && org.currency !== 'USD' ? org.currency : detectCurrency());
  const [regionOpen, setRegionOpen] = useState(false);

  // Step 2: first client
  const [clientName, setClientName]   = useState('');
  const [clientModel, setClientModel] = useState('hourly');
  const [clientRate, setClientRate]   = useState('');
  const [retainer, setRetainer]       = useState('');
  const [hours, setHours]             = useState('');
  const [overage, setOverage]         = useState('');
  const [importOpen, setImportOpen]   = useState(false);

  const isIndividual = accountType === 'individual';

  // ── SAVES ──────────────────────────────────────────────────────────────────

  const saveBasics = async () => {
    setErr('');
    if (!firmName.trim()) { setErr(`${isIndividual ? 'Your name or business name' : 'Organization name'} is required.`); return; }
    setSaving(true);
    try {
      // tagline '' keeps the sidebar on its built-in default rather than the column's
      // old 'Billing Dashboard'. Everything else (rates, billing increment, minimum
      // charge) stays at its database default until the person changes it in Settings.
      const updates = { name: firmName.trim(), tagline: '', timezone, currency, account_type: accountType };
      let id = orgId;
      if (!id) {
        const { data, error } = await supabase.rpc('create_org', { p_name: firmName.trim(), p_admin_email: session.user.email });
        if (error) throw error;
        id = data;
        setOrgId(id);
      }
      const { error } = await supabase.from('organizations').update(updates).eq('id', id);
      if (error) throw error;
      setStep(2);
    } catch (e) { setErr(e.message || 'Something went wrong saving that. Please try again.'); }
    setSaving(false);
  };

  const saveClient = async () => {
    setErr('');
    if (!clientName.trim()) { setErr('Client name is required.'); return; }
    setSaving(true);
    try {
      const data = { org_id: orgId, name: clientName.trim(), model: clientModel };
      if (clientModel === 'hourly')   data.rate     = parseFloat(clientRate) || 0;
      if (clientModel === 'retainer') data.retainer = parseFloat(retainer) || 0;
      if (clientModel === 'hybrid')   { data.retainer = parseFloat(retainer) || 0; data.hours = parseFloat(hours) || 0; data.overage = parseFloat(overage) || 0; }
      const { error } = await supabase.from('clients').insert(data);
      if (error) throw error;
      setStep(3);
    } catch (e) { setErr(e.message || 'Something went wrong saving that client. Please try again.'); }
    setSaving(false);
  };

  // `landOn` is where to send them: the Timer by default, or the Team page when they
  // chose "Invite your team". The Getting started panel opens on arrival either way.
  const finish = async (landOn = 'timer') => {
    setSaving(true); setErr('');
    const { error } = await supabase.from('organizations').update({ setup_complete: true }).eq('id', orgId);
    if (error) { setErr(error.message || 'Could not finish setup. Please try again.'); setSaving(false); return; }
    // Tells the dashboard this is a first arrival: open the checklist and hold the
    // page tour back so the two don't land on top of each other.
    localStorage.setItem('tk_gs_pending', orgId);
    window.location.hash = landOn;
    await onComplete();
    setSaving(false);
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <div style={css.page}>
      <div style={css.wrap}>

        <div style={{ textAlign:'center', marginBottom:'24px' }}>
          <div style={{ fontSize:'26px', fontWeight:'800', color:'#fff' }}>
            <span style={{ color:C.accent }}>Time</span>Keep
          </div>
          <div style={{ fontSize:'13px', color:C.muted, marginTop:'4px' }}>Let's get your account set up</div>
          {onBack && (
            <div style={{ marginTop:'10px' }}>
              <button type="button" style={css.skip} onClick={onBack}>← Back to {backLabel || 'your other workspace'}</button>
            </div>
          )}
        </div>

        <div style={css.card}>
          <ProgressBar step={step} />

          {/* ── STEP 0: Account type ─────────────────────────────────────── */}
          {step === 0 && (
            <>
              <h1 style={css.h1}>Welcome to TimeKeep</h1>
              <div style={css.sub}>How will you be using TimeKeep? This shapes what we set up for you.</div>

              <div role="radiogroup" aria-label="How you'll use TimeKeep" style={{ display:'flex', flexDirection:'column', gap:'12px', marginBottom:'28px' }}>
                {[
                  { id: 'organization', icon: '🏢', title: 'For my organization',
                    desc: 'You manage a team — contractors, employees, or both. You bill clients, track team time, and want insights across your whole operation.' },
                  { id: 'individual', icon: '👤', title: 'For myself',
                    desc: 'You work independently. You track your own time, bill your clients, and manage your own invoices — no team management needed.' },
                ].map(card => {
                  const active = accountType === card.id;
                  return (
                    <div key={card.id} role="radio" aria-checked={active} tabIndex={0}
                      onClick={() => setAccountType(card.id)}
                      onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setAccountType(card.id); } }}
                      style={{ display:'flex', alignItems:'flex-start', gap:'16px', padding:'16px 18px', borderRadius:'10px', cursor:'pointer', transition:'all 0.15s',
                        border:`2px solid ${active ? C.accent : C.border}`,
                        background: active ? 'rgba(0,160,200,0.07)' : C.bg }}>
                      <div style={{ fontSize:'28px', flexShrink:0, lineHeight:1, marginTop:'2px' }}>{card.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'15px', fontWeight:'700', color:C.textBright, marginBottom:'4px' }}>{card.title}</div>
                        <div style={{ fontSize:'13px', color:C.muted, lineHeight:'1.5' }}>{card.desc}</div>
                      </div>
                      <div style={{ width:'20px', height:'20px', borderRadius:'50%', flexShrink:0, marginTop:'2px', transition:'all 0.15s',
                        border:`2px solid ${active ? C.accent : C.muted}`,
                        background: active ? C.accent : 'transparent',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {active && <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#fff' }} />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {err && <div role="alert" style={css.error}>{err}</div>}
              <button
                onClick={() => { if (!accountType) { setErr('Please choose one to continue.'); return; } setErr(''); setStep(1); }}
                style={{ ...css.btn('primary'), width:'100%' }}>
                Continue →
              </button>
            </>
          )}

          {/* ── STEP 1: Basics ───────────────────────────────────────────── */}
          {step === 1 && (
            <form onSubmit={e => { e.preventDefault(); if (!saving) saveBasics(); }} noValidate>
              <h1 style={css.h1}>{isIndividual ? 'Your name' : 'Your organization'}</h1>
              <div style={css.sub}>
                {isIndividual
                  ? 'This is the name your clients see on invoices and emails.'
                  : 'This is the name your clients see on invoices and emails. You can add your logo and address later.'}
              </div>
              {err && <div role="alert" style={css.error}>{err}</div>}

              <div style={css.field}>
                <label htmlFor="setup-name" style={css.lbl}>{isIndividual ? 'Your name or business name' : 'Organization name'} *</label>
                <input id="setup-name" style={css.inp} placeholder={isIndividual ? 'Jane Smith Consulting' : 'Acme Consulting LLC'}
                  value={firmName} onChange={e => setFirmName(e.target.value)} autoFocus autoComplete="organization" />
              </div>

              <div style={{ ...css.help, marginTop:0, marginBottom: regionOpen ? '12px' : 0 }}>
                Timezone <strong style={{ color:C.text }}>{tzLabel(timezone)}</strong> · currency <strong style={{ color:C.text }}>{currency}</strong>
                {' '}(from your browser).{' '}
                <button type="button" style={css.link} onClick={() => setRegionOpen(o => !o)} aria-expanded={regionOpen}>
                  {regionOpen ? 'Done' : 'Change'}
                </button>
              </div>
              {regionOpen && (
                <div style={css.g2}>
                  <div>
                    <label htmlFor="setup-tz" style={css.lbl}>Timezone</label>
                    <select id="setup-tz" style={css.sel} value={timezone} onChange={e => setTimezone(e.target.value)}>
                      {tzOptions(timezone).map(tz => <option key={tz} value={tz}>{tzLabel(tz)}</option>)}
                    </select>
                    <div style={css.help}>Used to send notifications and build reports at the right local time.</div>
                  </div>
                  <div>
                    <label htmlFor="setup-cur" style={css.lbl}>Currency</label>
                    <select id="setup-cur" style={css.sel} value={currency} onChange={e => setCurrency(e.target.value)}>
                      {currencyOptions(currency).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div style={css.help}>Used for every amount and invoice.</div>
                  </div>
                </div>
              )}

              <div style={css.row}>
                <button type="button" style={css.btn()} onClick={() => { setErr(''); setStep(0); }}>← Back</button>
                <button type="submit" style={css.btn('primary')} disabled={saving}>{saving ? 'Saving…' : 'Continue →'}</button>
              </div>
            </form>
          )}

          {/* ── STEP 2: First client ─────────────────────────────────────── */}
          {step === 2 && (
            <form onSubmit={e => { e.preventDefault(); if (!saving) saveClient(); }} noValidate>
              <h1 style={css.h1}>Add your first client</h1>
              <div style={css.sub}>
                Clients are the organizations or people you bill. You can skip this and add them any time from the Clients page.
              </div>
              {err && <div role="alert" style={css.error}>{err}</div>}

              <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', borderRadius:'8px', border:`1px dashed ${C.border}`, marginBottom:'18px', background:'rgba(255,255,255,0.02)' }}>
                <span style={{ fontSize:'16px' }} aria-hidden="true">📥</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'13px', fontWeight:'600', color:C.textBright }}>Import clients from a spreadsheet</div>
                  <div style={{ fontSize:'11px', color:C.muted, marginTop:'2px' }}>Have a lot of clients already? Upload a CSV or Excel file instead.</div>
                </div>
                <button type="button" style={{ fontSize:'12px', background:'rgba(0,160,200,0.1)', color:'#63d3f7', border:'none', padding:'6px 12px', borderRadius:'6px', fontWeight:'600', whiteSpace:'nowrap', cursor:'pointer' }}
                  onClick={() => setImportOpen(true)}>Import…</button>
              </div>

              <div style={css.field}>
                <label htmlFor="setup-client" style={css.lbl}>Client name *</label>
                <input id="setup-client" style={css.inp} placeholder="Acme Corp" value={clientName} onChange={e => setClientName(e.target.value)} autoFocus />
              </div>

              <div style={{ marginBottom:'16px' }} role="radiogroup" aria-label="Billing model">
                <div style={css.lbl}>How do you bill them? *</div>
                <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginTop:'4px' }}>
                  {MODEL_CARDS.map(m => (
                    <div key={m.id} role="radio" aria-checked={clientModel === m.id} tabIndex={0}
                      onClick={() => setClientModel(m.id)}
                      onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setClientModel(m.id); } }}
                      style={{ display:'flex', alignItems:'flex-start', gap:'12px', padding:'12px 14px', borderRadius:'8px', cursor:'pointer', transition:'all 0.15s',
                        border:`2px solid ${clientModel === m.id ? C.accent : C.border}`,
                        background: clientModel === m.id ? 'rgba(0,160,200,0.07)' : C.bg }}>
                      <div style={{ fontSize:'18px', marginTop:'1px', flexShrink:0 }} aria-hidden="true">{m.icon}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'13px', fontWeight:'700', color:C.textBright, marginBottom:'2px' }}>{m.label}</div>
                        <div style={{ fontSize:'12px', color:C.muted, lineHeight:'1.4' }}>{m.desc}</div>
                      </div>
                      <div style={{ width:'16px', height:'16px', borderRadius:'50%', flexShrink:0, marginTop:'3px', transition:'all 0.15s',
                        border:`2px solid ${clientModel === m.id ? C.accent : C.muted}`,
                        background: clientModel === m.id ? C.accent : 'transparent' }} />
                    </div>
                  ))}
                </div>
              </div>

              {clientModel === 'hourly' && (
                <div style={css.field}>
                  <label htmlFor="setup-rate" style={css.lbl}>Hourly rate</label>
                  <input id="setup-rate" style={{ ...css.inp, maxWidth:'160px' }} type="number" min="0" step="0.01" inputMode="decimal" placeholder="e.g. 150" value={clientRate} onChange={e => setClientRate(e.target.value)} />
                  <div style={css.help}>What you charge this client per hour. You can change it later, with a start date.</div>
                </div>
              )}
              {clientModel === 'retainer' && (
                <div style={css.field}>
                  <label htmlFor="setup-retainer" style={css.lbl}>Monthly retainer</label>
                  <input id="setup-retainer" style={{ ...css.inp, maxWidth:'160px' }} type="number" min="0" step="0.01" inputMode="decimal" placeholder="e.g. 3000" value={retainer} onChange={e => setRetainer(e.target.value)} />
                  <div style={css.help}>A fixed amount invoiced each month, however many hours you work.</div>
                </div>
              )}
              {clientModel === 'hybrid' && (
                <div style={css.g2}>
                  <div>
                    <label htmlFor="setup-base" style={css.lbl}>Monthly base</label>
                    <input id="setup-base" style={css.inp} type="number" min="0" step="0.01" inputMode="decimal" placeholder="e.g. 2500" value={retainer} onChange={e => setRetainer(e.target.value)} />
                    <div style={css.help}>Flat fee that covers the included hours.</div>
                  </div>
                  <div>
                    <label htmlFor="setup-hours" style={css.lbl}>Included hours</label>
                    <input id="setup-hours" style={css.inp} type="number" min="0" step="1" inputMode="decimal" placeholder="e.g. 20" value={hours} onChange={e => setHours(e.target.value)} />
                    <div style={css.help}>Hours covered per month.</div>
                  </div>
                  <div style={{ gridColumn:'1 / -1' }}>
                    <label htmlFor="setup-overage" style={css.lbl}>Overage rate</label>
                    <input id="setup-overage" style={{ ...css.inp, maxWidth:'160px' }} type="number" min="0" step="0.01" inputMode="decimal" placeholder="e.g. 150" value={overage} onChange={e => setOverage(e.target.value)} />
                    <div style={css.help}>Per hour, for every hour beyond the included amount.</div>
                  </div>
                </div>
              )}

              <div style={css.row}>
                <button type="button" style={css.btn()} onClick={() => { setErr(''); setStep(1); }}>← Back</button>
                <button type="submit" style={css.btn('primary')} disabled={saving}>{saving ? 'Saving…' : 'Add client →'}</button>
              </div>
              <div>
                <button type="button" style={css.skip} onClick={() => { setErr(''); setStep(3); }}>Skip for now →</button>
              </div>
            </form>
          )}

          {/* ── STEP 3: Done ─────────────────────────────────────────────── */}
          {step === 3 && (
            <>
              <div style={{ textAlign:'center', marginBottom:'22px' }}>
                <div style={{ fontSize:'44px', marginBottom:'10px' }} aria-hidden="true">🎉</div>
                <h1 style={css.h1}>You're all set</h1>
                <div style={{ ...css.sub, marginBottom:0 }}>
                  Your workspace is ready. A short <strong style={{ color:C.text }}>Getting started</strong> checklist
                  will be waiting inside, with your rates, invoice details{isIndividual ? '' : ', team'} and more. Do it now or any time; you can hide it and bring it back from the sidebar.
                </div>
              </div>

              {err && <div role="alert" style={css.error}>{err}</div>}

              <button type="button" style={{ ...css.btn('primary'), width:'100%', padding:'12px 22px', fontSize:'14px' }} onClick={() => finish('timer')} disabled={saving}>
                {saving ? 'Finishing…' : 'Go to TimeKeep →'}
              </button>

              <div style={{ marginTop:'18px', fontSize:'11px', color:C.muted, textTransform:'uppercase', letterSpacing:'0.5px', fontWeight:600 }}>Optional extras</div>
              <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginTop:'8px' }}>
                {!isIndividual && (
                  <button type="button" onClick={() => finish('team')} disabled={saving}
                    style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', borderRadius:'8px', border:`1px solid ${C.border}`, background:C.bg, cursor:'pointer', textAlign:'left', fontFamily:'inherit' }}>
                    <span style={{ fontSize:'20px' }} aria-hidden="true">👥</span>
                    <span style={{ flex:1 }}>
                      <span style={{ display:'block', fontSize:'13px', fontWeight:700, color:C.textBright }}>Invite your team</span>
                      <span style={{ display:'block', fontSize:'12px', color:C.muted, marginTop:'2px' }}>Opens the Team page, where you can add contractors, employees or admins.</span>
                    </span>
                  </button>
                )}
                <a href={CHROME_STORE_URL} target="_blank" rel="noreferrer"
                  style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', borderRadius:'8px', border:`1px solid ${C.border}`, background:C.bg, textDecoration:'none' }}>
                  <span style={{ fontSize:'20px' }} aria-hidden="true">🧩</span>
                  <span style={{ flex:1 }}>
                    <span style={{ display:'block', fontSize:'13px', fontWeight:700, color:C.textBright }}>Add the Chrome extension</span>
                    <span style={{ display:'block', fontSize:'12px', color:C.muted, marginTop:'2px' }}>Start and stop timers from Gmail, Google Calendar, Asana and more. Opens in a new tab.</span>
                  </span>
                </a>
              </div>
            </>
          )}
        </div>

        {/* Signed in as the wrong person? Without this the only way out was clearing site data. */}
        {onSignOut && (
          <div style={{ textAlign:'center', marginTop:'16px', fontSize:'12px', color:C.muted }}>
            Signed in as {session?.user?.email}. <button type="button" style={css.link} onClick={onSignOut}>Sign out</button>
          </div>
        )}
      </div>
      {importOpen && (
        <BulkImportModal entityType="clients" org={{ id: orgId, name: firmName }} profile={profile}
          onClose={() => setImportOpen(false)} onComplete={() => {}} />
      )}
    </div>
  );
}
